import React, { useContext, useEffect, useRef, useState } from 'react';
import { useApolloClient, useMutation, useQuery } from '@apollo/client/react';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { CompositeFilterDescriptor, getter, orderBy } from '@progress/kendo-data-query';
import { GridDataType, GridSelectionChangeEvent } from '@progress/kendo-react-grid';
import BulkUpdateDialog from '@components-clean/core/bulk-update-dialog/bulk-update-dialog';
import {
    IOpenCommentsCountChangedMessage,
    OPEN_COMMENTS_COUNT_CHANGED,
} from '@components-clean/core/comments-side-bar';
import ConfirmationDialog from '@components-clean/core/confirmation-dialog';
import ErrorMessage from '@components-clean/core/error';
import useGridCustomCell, { INamedItem } from '@components-clean/core/grid/custom-cell';
import Grid from '@components-clean/core/grid-fe';
import { DateTimeCell, YesNoCell } from '@components-clean/core/grid-fe/custom-cells';
import { GridColumnProps } from '@components-clean/core/grid-fe/grid.interface';
import Loading from '@components-clean/core/loading/loading';
import { ComponentNames, useUnsavedChangesContext } from '@contexts-clean/unsaved-changes-context';
import { SelectedFieldValuesProps } from '@interfaces-clean/bulk-update.interface';
import AddCriticaltoQualityFactorDialog, {
    CtQfFormInputs,
} from '@pages-clean/risk-assessment/critical-to-quality-factors/dialogs/add-critical-to-quality-factor-dialog';
import AddRiskDialog, {
    RiskFormInputs,
} from '@pages-clean/risk-assessment/critical-to-quality-factors/dialogs/add-risk-dialog';
import AssignFunctionalDataReviewToCtQfDialog, {
    ILibraryFunctionalDataReviewFieldsSubset,
} from '@pages-clean/risk-assessment/critical-to-quality-factors/dialogs/assign-fdrs-to-ctqf-dialog';
import AssignRisksToCtqfDialog, {
    ILibraryRiskFieldsSubset,
} from '@pages-clean/risk-assessment/critical-to-quality-factors/dialogs/assign-risks-to-ctqf-dialog';
// eslint-disable-next-line max-len
import DeleteCriticalToQualityFactorDialog from '@pages-clean/risk-assessment/critical-to-quality-factors/dialogs/delete-critical-to-quality-factor-dialog';
// eslint-disable-next-line max-len
import EditCriticaltoQualityFactorDialog from '@pages-clean/risk-assessment/critical-to-quality-factors/dialogs/edit-critical-to-quality-factor-dialog';
import { getAssignableRisks, getFilteredRisks } from '@pages-clean/risk-assessment/helpers';
import { CTQFactorsContext, SelectedRowsProps } from '@pages-clean/risk-assessment/interfaces/context';
import { useEventBus } from '@utils-clean/eventBus';
import { PersistentStorageKeys } from '@utils-clean/persistent-storage-keys';
import {
    AuditApplicationArea,
    AuditDataType,
    AuditServiceType,
    CountryInputType,
    NamedItemType,
    RiskStatusType,
    TimeFrequencyType,
} from 'types/graphql-global-types';
import { Permissions } from '@contexts/user-context';
import { useAuthorization, useBoolean, useTranslation } from '@hooks/index';
import Constants from '@utils/constants';
import { stringDateWithoutTimeFromCurrentUtcDate, stringDateWithoutTimeFromLocal } from '@utils/convertor';
import { ShowError, ShowMessage, ShowSuccess } from '@utils/notification';
import { GET_NAMED_ITEMS_BY_TYPE } from '@queries/common';
import { getNamedItemsByType } from '@queries/generated/common.generated';
import { CriticalVariableFields } from '@queries/generated/fragments.generated';
import {
    bulkUpdateCriticalVariables,
    bulkUpdateCriticalVariablesVariables,
    createCriticalVariable,
    createCriticalVariableVariables,
    updateCriticalVariable,
    updateCriticalVariableVariables,
    createRiskAndAssignCriticalToQualityFactor,
    createRiskAndAssignCriticalToQualityFactorVariables,
    assignStudyAndLibraryRisks,
    assignStudyAndLibraryRisksVariables,
    assignLibraryFunctionalDataReview,
    assignLibraryFunctionalDataReviewVariables,
} from '@queries/generated/risk-assessment.generated';
import {
    GET_CRITICAL_VARIABLES,
    BULK_UPDATE_CRITICAL_VARIABLES,
    ADD_CRITICAL_TO_QUALITY_FACTOR,
    UPDATE_CRITICAL_TO_QUALITY_FACTOR,
    ASSIGN_STUDY_AND_LIBRARY_RISKS,
    GET_CRITICAL_VARIABLE_ASSOCIATED_RISKS,
    CTQ_WIDGET_VALUES,
    ASSIGN_LIBRARY_FUNCTIONAL_DATA_REVIEWS,
    GET_LIBRARY_RISKS,
    ADD_RISK_AND_ASSIGN_CRITICAL_TO_QUALITY_FACTOR,
    GET_FUNCTIONAL_DATA_REVIEWS_BY_STUDY_AND_CTQ,
} from '@queries/risk-assessment';
import { GET_RISK_LIBRARY_CATEGORIES } from '@queries/studies';

interface CriticalToQualityGridProps {
    setSelectedRiskId: React.Dispatch<React.SetStateAction<string | null>>;
}

const CriticalToQualityGrid = ({ setSelectedRiskId }: CriticalToQualityGridProps) => {
    const { t } = useTranslation('ram', { keyPrefix: 'ctqf.ctqFactors' });
    const { t: tCommon } = useTranslation('common');
    const { current, setCurrent, setSelectionState } = useContext(CTQFactorsContext);
    const { confirmDiscardChanges, componentHasChanges } = useUnsavedChangesContext();
    const { study } = current;
    const showAddDialog = useBoolean(false);
    const showEditDialog = useBoolean(false);
    const showDeleteDialog = useBoolean(false);
    const showAssignDialog = useBoolean(false);
    const showAddRiskDialog = useBoolean(false);
    const showAssignFunctionalDataReviewsDialog = useBoolean(false);

    const ctQfId = useRef<string | null>(null);
    const bulkUpdateAction = useRef(false);
    const didInitialDataLoad = useRef(false);
    const scrollToSelectedCtq = useRef(false);
    const createdOrUpdatedCtqId = useRef<string | null>(null);
    const applyingDefaultSelection = useRef(false);
    const assignRiskClicked = useRef(false);

    const [ctqFactor, setCtqFactor] = useState<CriticalVariableFields>();
    const [filterState, setFilterState] = useState<CompositeFilterDescriptor | undefined>({
        logic: 'and',
        filters: [],
    });

    const [saveCriticalToQualityFactor] = useMutation<createCriticalVariable, createCriticalVariableVariables>(
        ADD_CRITICAL_TO_QUALITY_FACTOR,
    );

    const [saveRiskAndAssignCtQF] = useMutation<
        createRiskAndAssignCriticalToQualityFactor,
        createRiskAndAssignCriticalToQualityFactorVariables
    >(ADD_RISK_AND_ASSIGN_CRITICAL_TO_QUALITY_FACTOR);

    const [updateCriticalToQualityFactor] = useMutation<updateCriticalVariable, updateCriticalVariableVariables>(
        UPDATE_CRITICAL_TO_QUALITY_FACTOR,
    );

    const [updateCtQfRisks, { loading: updatingRisks }] = useMutation<
        assignStudyAndLibraryRisks,
        assignStudyAndLibraryRisksVariables
    >(ASSIGN_STUDY_AND_LIBRARY_RISKS);

    const [updateCtQfFunctionalDataReviews] = useMutation<
        assignLibraryFunctionalDataReview,
        assignLibraryFunctionalDataReviewVariables
    >(ASSIGN_LIBRARY_FUNCTIONAL_DATA_REVIEWS);

    const [saveBulkUpdates] = useMutation<bulkUpdateCriticalVariables, bulkUpdateCriticalVariablesVariables>(
        BULK_UPDATE_CRITICAL_VARIABLES,
    );

    const client = useApolloClient();
    const { loading, error, data, refetch } = useQuery(GET_CRITICAL_VARIABLES, {
        variables: { studyId: study?.id },
    });

    useEventBus<IOpenCommentsCountChangedMessage>(OPEN_COMMENTS_COUNT_CHANGED, ({ parentId, open, all }) => {
        client.cache.modify({
            id: `CriticalVariable:${parentId}`,
            fields: {
                commentCounts: () => ({ all, open }),
            },
        });
    });

    const { data: edcEntryData } = useQuery<getNamedItemsByType>(GET_NAMED_ITEMS_BY_TYPE, {
        variables: { type: NamedItemType.CTQEDC_ENTRY },
    });
    const { data: responsibleVendorData } = useQuery<getNamedItemsByType>(GET_NAMED_ITEMS_BY_TYPE, {
        variables: { type: NamedItemType.VENDOR },
    });
    const { data: ctqTypeData } = useQuery<getNamedItemsByType>(GET_NAMED_ITEMS_BY_TYPE, {
        variables: { type: NamedItemType.CTQ_TYPE },
    });
    const { data: libraryRisks, loading: loadingLibraryRisks } = useQuery(GET_LIBRARY_RISKS, {
        variables: { organisationId: study!.organisation?.id, includeSystemRisks: false },
    });
    const { data: riskLibraryCategories, loading: loadingRiskLibraryCategories } = useQuery(
        GET_RISK_LIBRARY_CATEGORIES,
        {
            variables: { id: study!.id },
        },
    );

    const [flattenedCriticalVariables, setFlattenedCriticalVariables] = useState<CriticalVariableFields[]>([]);
    const { TooltipCell, ActiveInactiveCell } = useGridCustomCell();
    const exportFileName = t('grid.exportFileName');
    const showBulkUpdateDialog = useBoolean(false);
    const showFilterAppliedDialog = useBoolean(false);

    const user = useAuthorization();
    const canView = user?.canRead?.({
        module: Constants.Modules.RAM,
        element: Constants.DataElements.RiskAssessment,
        permission: Permissions.Read,
    });
    const canEdit = user?.canWrite?.({
        module: Constants.Modules.RAM,
        element: Constants.DataElements.RiskAssessment,
        permission: Permissions.ReadWrite,
    });
    const hasCommentsPermission = user?.can?.({
        module: Constants.Modules.RAM,
        permission: Permissions.Enabled,
        element: Constants.PageElements.CtqComments,
    });

    if (!canView) return null;

    const assignableRisks = React.useMemo(() => {
        if (!riskLibraryCategories.study || loading || loadingLibraryRisks || loadingRiskLibraryCategories) return [];
        return (
            getAssignableRisks({
                data,
                libraryRisks,
                riskLibraryCategories,
                ctqFactor,
            }) ?? []
        );
    }, [
        data,
        libraryRisks,
        riskLibraryCategories,
        ctqFactor,
        loading,
        loadingLibraryRisks,
        loadingRiskLibraryCategories,
    ]);

    const discardCommentSideBarChanges = async () => {
        if (!componentHasChanges(ComponentNames.commentsSideBar)) {
            return true;
        }

        return confirmDiscardChanges();
    };

    useEffect(() => {
        if (!edcEntryData || !responsibleVendorData || !ctqTypeData || !data || !data.criticalVariables) return;

        const array = data.criticalVariables.map((item: CriticalVariableFields) => {
            return {
                ...item,
                vendorList: item.vendors?.map((a) => a?.name).join(', '),
                createdBy: item.createdBy ? `${item.createdBy.firstName} ${item.createdBy.lastName}` : '',
                updatedBy: item.updatedBy ? `${item.updatedBy.firstName} ${item.updatedBy.lastName}` : '',
            };
        });

        const sortedResult = orderBy(array, [
            { field: 'active', dir: 'desc' },
            { field: 'isCritical', dir: 'desc' },
        ]) as unknown as CriticalVariableFields[];

        if (!didInitialDataLoad.current) {
            setSelectionState((old) => ({
                ...old,
                selectedItemsCount: 0,
                bulkUpdateRows: [],
                selectedItem: undefined,
            }));
            setCtqFactor(undefined);
            scrollToSelectedCtq.current = false;
        }

        let selectedItem: CriticalVariableFields | undefined;

        if (createdOrUpdatedCtqId.current) {
            const found = sortedResult.find((cv) => cv.id === createdOrUpdatedCtqId.current);
            if (found) {
                selectedItem = found;
                createdOrUpdatedCtqId.current = null;
            }
        } else if (ctQfId.current) {
            const selectedId = ctQfId.current;
            ctQfId.current = null;
            selectedItem = sortedResult.find((cv) => cv.id === selectedId);
        } else if (
            didInitialDataLoad.current &&
            current.selectionState.selectedItem?.id &&
            !current.selectionState.bulkUpdateRows?.length
        ) {
            selectedItem = sortedResult.find((cv) => cv.id === current.selectionState.selectedItem?.id);
        }

        setFlattenedCriticalVariables(sortedResult);

        if (selectedItem) {
            if (current.selectionState.selectedItemsCount === 1) {
                scrollToSelectedCtq.current = true;
                applyingDefaultSelection.current = true;
            }

            setSelectionState((old) => ({
                ...old,
                selectedItem,
                selectedItemsCount: 1,
                bulkUpdateRows: [{ id: selectedItem?.id }],
            }));
            setCtqFactor(selectedItem);
        }

        if (!didInitialDataLoad.current) {
            didInitialDataLoad.current = true;
        }
    }, [data, edcEntryData, responsibleVendorData, ctqTypeData]);

    const onSelectionChange = (
        event: GridSelectionChangeEvent,
        selectedState: { [id: string]: boolean | number[] },
    ) => {
        let selectedRowCount = 0;
        let selectedItem: CriticalVariableFields | undefined;
        let selectedItemsProps: SelectedRowsProps[] = [];

        if (Object.keys(selectedState).length !== 0) {
            const idGetter = getter('id') ?? '';
            const selectedRows = flattenedCriticalVariables.filter((x) => selectedState[idGetter(x)] === true);
            selectedRowCount = selectedRows.length;
            selectedItemsProps = selectedRows.map((item: CriticalVariableFields) => ({
                id: idGetter(item) as number,
            }));
            selectedItem = flattenedCriticalVariables.find((x) => selectedState[idGetter(x)] === true) as
                | CriticalVariableFields
                | undefined;

            if (selectedRowCount > 1) {
                selectedItem = undefined;
                setCtqFactor(undefined);
            } else if (selectedRowCount === 1) {
                setCtqFactor(selectedItem);
            } else {
                setCtqFactor(undefined);
            }
        } else {
            if (applyingDefaultSelection.current) {
                applyingDefaultSelection.current = false;
                return;
            }

            selectedItem = undefined;
            setCtqFactor(undefined);
            scrollToSelectedCtq.current = false;
        }

        if (selectedItem && selectedItem.id !== current.selectionState.selectedItem?.id) {
            scrollToSelectedCtq.current = false;
        }

        setSelectionState((old) => ({
            ...old,
            selectedItemsCount: selectedRowCount,
            bulkUpdateRows: selectedItemsProps,
            selectedItem,
        }));
    };

    const onBulkUpdateClicked = () => {
        if (current.selectionState.bulkUpdateRows.length < 1) return;
        showBulkUpdateDialog.setTrue();
    };

    const onSubmitBulkUpdate = async (selectedFieldsData: SelectedFieldValuesProps[]) => {
        try {
            const getFieldValue = (field: string) => {
                const fieldObj = selectedFieldsData.find((f) => f.field === field);
                if (fieldObj) {
                    if (fieldObj.type === 'boolean') {
                        return fieldObj.value === 'True';
                    }

                    if ((fieldObj.type === 'ctqTypeLov' || fieldObj.type === 'edcEntryLov') && fieldObj.value === '') {
                        /* For the LOV items they can either be null ( not selected in the form).
                         empty, so we need to pass an empty ObjectId so the backend knows to clear and not make it mull */
                        return '000000000000000000000000';
                    }

                    return fieldObj.value;
                }
                return null;
            };
            const response = await saveBulkUpdates({
                variables: {
                    criticalVariableIds: current.selectionState.bulkUpdateRows.map((item) => item.id),
                    active: getFieldValue('active') as boolean,
                    critical: getFieldValue('critical') as boolean,
                    datasource: getFieldValue('dataSource') as string,
                    edcEntry: getFieldValue('edcEntry'),
                    responsibleVendors: getFieldValue('responsibleVendors') as string[],
                    studyId: study?.id,
                    system: getFieldValue('system') as string,
                    type: getFieldValue('type'),
                    justification: getFieldValue('justification') as string,
                },
                refetchQueries: [
                    {
                        query: CTQ_WIDGET_VALUES,
                        variables: { studyId: study?.id },
                    },
                ],
            });

            if (response) {
                bulkUpdateAction.current = true;
                ShowMessage({ text: tCommon('common:actionsSuccessfulToast'), type: 'success' });
                await refetch();
            } else {
                ShowError(tCommon('common:errorMessage'));
            }
        } catch {
            ShowError(tCommon('common:errorMessage'));
        } finally {
            showBulkUpdateDialog.setFalse();
        }
    };

    const addCallback = () => {
        createdOrUpdatedCtqId.current = null;
        scrollToSelectedCtq.current = false;
        showAddDialog.setTrue();
    };

    const onSaveCriticalToQualityFactor = async (addData: CtQfFormInputs, assignFdrs: boolean) => {
        saveCriticalToQualityFactor({
            variables: {
                criticalVariable: {
                    studyId: study?.id,
                    active: addData.isActive,
                    description: addData.description,
                    justification: addData.justification,
                    typeId: addData.type?.id,
                    edcentryId: addData.edcEntry?.id,
                    vendorIds: addData.vendors?.map((v) => v.id) ?? [],
                    sourceSystem: addData.system,
                    dataSource: addData.dataSource,
                    isCritical: addData.isCritical,
                },
            },
            refetchQueries: [
                {
                    query: CTQ_WIDGET_VALUES,
                    variables: { studyId: study?.id },
                },
            ],
        })
            .then(async (response) => {
                if (!response?.data?.createCriticalVariable?.id) {
                    ShowError(tCommon('errorMessage'));
                    return;
                }
                showAddDialog.setFalse();

                createdOrUpdatedCtqId.current = response.data.createCriticalVariable.id;
                scrollToSelectedCtq.current = true;

                await refetch();
                setFilterState({
                    logic: 'and',
                    filters: [],
                });
                ShowSuccess(tCommon('actionsSuccessfulToast'));
                if (assignFdrs) showAssignFunctionalDataReviewsDialog.setTrue();
            })
            .catch(() => {
                ShowError(tCommon('errorMessage'));
            });
    };

    const onSubmit = async (addData: CtQfFormInputs) => {
        await onSaveCriticalToQualityFactor(addData, false);
    };

    const onAddFunctionalReviewsSubmit = async (addData: CtQfFormInputs) => {
        await onSaveCriticalToQualityFactor(addData, true);
    };

    const onAddClose = () => {
        showAddDialog.setFalse();
    };
    const onEditClose = () => {
        showEditDialog.setFalse();
    };

    const onSubmitEdit = async (updateData: CtQfFormInputs) => {
        updateCriticalToQualityFactor({
            variables: {
                criticalVariable: {
                    studyId: study?.id,
                    active: updateData.isActive,
                    description: updateData.description,
                    justification: updateData.justification,
                    typeId: updateData.type?.id,
                    edcentryId: updateData.edcEntry?.id,
                    vendorIds: updateData.vendors?.map((v) => v.id) ?? [],
                    sourceSystem: updateData.system,
                    dataSource: updateData.dataSource,
                    isCritical: updateData.isCritical,
                    id: ctqFactor?.id,
                },
            },
            refetchQueries: [
                {
                    query: CTQ_WIDGET_VALUES,
                    variables: { studyId: study?.id },
                },
            ],
        })
            .then(async (response) => {
                if (!response?.data?.updateCriticalVariable) {
                    ShowError(tCommon('errorMessage'));
                    return;
                }
                showEditDialog.setFalse();

                if (ctqFactor?.id) {
                    createdOrUpdatedCtqId.current = ctqFactor.id;
                    scrollToSelectedCtq.current = true;
                }

                await refetch();
                ShowSuccess(tCommon('actionsSuccessfulToast'));
            })
            .catch(() => {
                ShowError(tCommon('errorMessage'));
            });
    };

    const submitDelete = async (result: string) => {
        showDeleteDialog.setFalse();
        if (result === 'error') {
            ShowError(tCommon('errorMessage'));
        } else {
            if (result === 'success') {
                setSelectionState((old) => ({
                    ...old,
                    selectedItemsCount: 0,
                    bulkUpdateRows: [],
                    selectedItem: undefined,
                }));
                setCtqFactor(undefined);
                scrollToSelectedCtq.current = false;
                ctQfId.current = null;
                createdOrUpdatedCtqId.current = null;
            } else {
                ctQfId.current = ctqFactor?.id ?? null;
                scrollToSelectedCtq.current = true;
            }
            await refetch();
            ShowSuccess(tCommon('actionsSuccessfulToast'));
        }
    };

    const assignRisksToCtQf = async (
        selectedStudyRiskIds: string[],
        selectedLibraryRisks: ILibraryRiskFieldsSubset[],
        closeDialog: boolean,
    ) => {
        if (!ctqFactor) return;
        let ids = ctqFactor?.associatedRiskIds ?? [];

        ids = ids.concat(selectedStudyRiskIds.filter((item: string) => ids.indexOf(item) < 0).map((r) => r));

        // Make sure we don't have any duplicates
        ids = [...Array.from(new Set(ids))];

        updateCtQfRisks({
            variables: {
                id: ctqFactor?.id,
                studyRiskIds: ids,
                libraryRisks: selectedLibraryRisks,
            },
            refetchQueries: [
                {
                    query: GET_CRITICAL_VARIABLE_ASSOCIATED_RISKS,
                    variables: {
                        studyId: study?.id,
                        criticalVariableId: ctqFactor?.id,
                    },
                },
                {
                    query: CTQ_WIDGET_VALUES,
                    variables: { studyId: study?.id },
                },
            ],
        })
            .then(async (response) => {
                if (!response?.data || !response.data?.assignStudyAndLibraryRisks) {
                    ShowError(tCommon('errorMessage'));
                    return;
                }

                ctQfId.current = ctqFactor?.id ?? null;
                scrollToSelectedCtq.current = true;

                if (closeDialog) {
                    showAssignDialog.setFalse();
                    showAddRiskDialog.setFalse();
                }

                setCurrent((prevState) => ({
                    ...prevState,
                    riskGridIsFiltered: false,
                }));

                await refetch();
                setCtqFactor((old) =>
                    old
                        ? {
                              ...old,
                              associatedRiskIds: response.data?.assignStudyAndLibraryRisks?.associatedRiskIds ?? [],
                          }
                        : undefined,
                );
                ShowSuccess(tCommon('actionsSuccessfulToast'));
            })
            .catch(() => {
                ShowError(tCommon('errorMessage'));
            });
    };

    const onSubmitAssignRisks = async (
        selectedStudyRiskIds: string[],
        close: boolean,
        selectedLibraryRisks: ILibraryRiskFieldsSubset[],
    ) => {
        assignRiskClicked.current = true;
        await assignRisksToCtQf(selectedStudyRiskIds, selectedLibraryRisks, close);
    };

    const assignFunctionalDataReviewsCtQf = async (
        closeDialog: boolean,
        functionalDataReviews: ILibraryFunctionalDataReviewFieldsSubset[],
    ) => {
        if (!ctqFactor) return;
        updateCtQfFunctionalDataReviews({
            variables: {
                id: ctqFactor?.id,
                libraryFdrs: functionalDataReviews,
            },
            refetchQueries: [
                {
                    query: GET_FUNCTIONAL_DATA_REVIEWS_BY_STUDY_AND_CTQ,
                    variables: {
                        studyId: study?.id,
                        ctqId: ctqFactor?.id,
                    },
                },
                {
                    query: CTQ_WIDGET_VALUES,
                    variables: { studyId: study?.id },
                },
            ],
        })
            .then(async (response) => {
                if (!response?.data?.assignLibraryFunctionalDataReview) {
                    ShowError(tCommon('errorMessage'));
                    return;
                }

                ctQfId.current = ctqFactor?.id ?? null;
                scrollToSelectedCtq.current = true;

                if (closeDialog) showAssignFunctionalDataReviewsDialog.setFalse();

                setCurrent((prevState) => ({
                    ...prevState,
                    functionalDataReviewsGridIsFiltered: false,
                }));

                await refetch();
                setCtqFactor((old) =>
                    old
                        ? {
                              ...old,
                              associatedFunctionalDataReviewIds:
                                  response.data?.assignLibraryFunctionalDataReview?.associatedFunctionalDataReviewIds ??
                                  [],
                              associatedFdrLibraryIds:
                                  response.data?.assignLibraryFunctionalDataReview?.associatedFdrLibraryIds ?? [],
                          }
                        : undefined,
                );
                ShowSuccess(tCommon('actionsSuccessfulToast'));
            })
            .catch(() => {
                ShowError(tCommon('errorMessage'));
            });
    };

    const onSubmitAssignFunctionalDataReviews = async (
        close: boolean,
        functionalDataReviews: ILibraryFunctionalDataReviewFieldsSubset[],
    ) => {
        await assignFunctionalDataReviewsCtQf(close, functionalDataReviews);
    };

    const onAssignClose = () => {
        showAssignDialog.setFalse();
        showAddRiskDialog.setFalse();
    };

    const onAddRiskOpen = () => {
        assignRiskClicked.current = false;
        showAddRiskDialog.setTrue();
    };

    const onSubmitAddRisk = async (addRiskData: RiskFormInputs) => {
        saveRiskAndAssignCtQF({
            variables: {
                risk: {
                    studyId: study?.id,
                    ownerId: addRiskData.owner?.id,
                    controlStrategyId: addRiskData.controlStrategy?.id,
                    categoryId: addRiskData.category?.id,
                    responsibleFunctionId: addRiskData.responsibleFunction?.id,
                    statement: addRiskData.statement,
                    dateIdentified: addRiskData?.dateIdentified
                        ? stringDateWithoutTimeFromLocal(addRiskData.dateIdentified)
                        : null,
                    closedDate: addRiskData?.closedDate ? stringDateWithoutTimeFromLocal(addRiskData.closedDate) : null,
                    reviewRequired: addRiskData.reviewRequired,
                    reviewFrequencyPeriod: addRiskData.reviewRequired ? addRiskData.reviewFrequencyPeriod : null,
                    reviewFrequencyUnits: addRiskData.reviewRequired
                        ? TimeFrequencyType[addRiskData.reviewFrequencyUnit?.id as keyof typeof TimeFrequencyType]
                        : null,
                    vendorIds: addRiskData.vendors?.map((v) => v.id) ?? [],
                    countries: addRiskData.countries?.map((c) => c as CountryInputType) ?? [],
                    internalOnly: addRiskData.internal,
                    status: RiskStatusType[addRiskData.status?.id as keyof typeof RiskStatusType],
                    qualityIssueId: addRiskData.qualityIssue?.id,
                    qualityIssueReference: addRiskData.qualityIssueReference,
                },
                criticalToQualityFactorId: ctqFactor?.id,
            },
            refetchQueries: [
                {
                    query: GET_CRITICAL_VARIABLE_ASSOCIATED_RISKS,
                    variables: {
                        studyId: study?.id,
                        criticalVariableId: ctqFactor?.id,
                    },
                },
                {
                    query: CTQ_WIDGET_VALUES,
                    variables: { studyId: study?.id },
                },
            ],
        })
            .then(async (response) => {
                if (!response?.data?.createRiskAndAssignCriticalToQualityFactor) {
                    ShowError(tCommon('errorMessage'));
                    return;
                }
                setSelectedRiskId(response.data?.createRiskAndAssignCriticalToQualityFactor?.id ?? null);

                ctQfId.current = ctqFactor?.id ?? null;
                scrollToSelectedCtq.current = true;

                showAssignDialog.setFalse();
                showAddRiskDialog.setFalse();

                setCurrent((prevState) => ({
                    ...prevState,
                    riskGridIsFiltered: false,
                }));

                await refetch();
                setCtqFactor((old) =>
                    old
                        ? {
                              ...old,
                              associatedRiskIds: [
                                  ...(old.associatedRiskIds ?? []),
                                  response.data?.createRiskAndAssignCriticalToQualityFactor?.id,
                              ],
                          }
                        : undefined,
                );
                ShowSuccess(tCommon('actionsSuccessfulToast'));
            })
            .catch(() => {
                ShowError(tCommon('errorMessage'));
            });
    };

    const onAssignFdRClose = () => {
        showAssignFunctionalDataReviewsDialog.setFalse();
    };

    const columns: GridColumnProps[] = [
        {
            id: 'risks',
            field: 'riskCount',
            title: t('grid.columns.risks'),
            lockedByDefault: true,
            width: 1,
            minWidth: 80,
            filter: 'numeric',
        },
        {
            id: 'fdrs',
            field: 'fDRCount',
            title: t('grid.columns.fdrs'),
            lockedByDefault: true,
            width: 1,
            minWidth: 60,
            filter: 'numeric',
        },
        ...(hasCommentsPermission
            ? [
                  {
                      id: 'openComments',
                      field: 'commentCounts.open',
                      title: t('grid.columns.openComments'),
                      lockedByDefault: true,
                      width: 1,
                      minWidth: 130,
                      filter: 'numeric' as GridDataType,
                  },
              ]
            : []),
        {
            id: 'description',
            field: 'description',
            title: t('grid.columns.description'),
            lockedByDefault: true,
            width: 1,
            minWidth: 300,
            filter: 'text',
            cells: {
                data: TooltipCell({
                    isCsv: false,
                }),
            },
        },
        {
            id: 'active',
            field: 'active',
            title: t('grid.columns.active'),
            locked: false,
            cells: { data: YesNoCell },
            width: 1,
            minWidth: 100,
            filter: 'boolean',
        },
        {
            id: 'type',
            field: 'cTQType',
            title: t('grid.columns.type'),
            locked: false,
            width: 1,
            minWidth: 120,
            filter: 'text',
            filterKey: 'name',
            lovData: ctqTypeData?.namedItemsByType
                ? ctqTypeData.namedItemsByType.map((item) => item?.name as string)
                : [],
            cells: {
                data: ActiveInactiveCell({
                    inactive: (dataItem: INamedItem) => dataItem?.active === false,
                    renderValue: (dataItem: INamedItem) => dataItem?.name,
                }),
            },
        },
        {
            id: 'justification',
            field: 'justification',
            title: t('grid.columns.justification'),
            locked: false,
            width: 1,
            minWidth: 200,
            filter: 'text',
            cells: {
                data: TooltipCell({
                    isCsv: false,
                }),
            },
        },
        {
            id: 'system',
            field: 'sourceSystem',
            title: t('grid.columns.sourceSystem'),
            locked: false,
            width: 1,
            minWidth: 150,
            filter: 'text',
            cells: {
                data: TooltipCell({
                    isCsv: false,
                }),
            },
        },
        {
            id: 'datasource',
            field: 'dataSource',
            title: t('grid.columns.dataSource'),
            locked: false,
            width: 1,
            minWidth: 100,
            filter: 'text',
            cells: {
                data: TooltipCell({
                    isCsv: false,
                }),
            },
        },
        {
            id: 'edcEntry',
            field: 'cTQEDCEntry',
            title: t('grid.columns.edcEntry'),
            locked: false,
            minWidth: 150,
            width: 1,
            filter: 'text',
            filterKey: 'name',
            lovData: edcEntryData?.namedItemsByType
                ? edcEntryData.namedItemsByType.map((item) => item?.name as string)
                : [],
            cells: {
                data: ActiveInactiveCell({
                    inactive: (dataItem: INamedItem) => dataItem?.active === false,
                    renderValue: (dataItem: INamedItem) => dataItem?.name,
                }),
            },
        },
        {
            id: 'vendors',
            field: 'vendorList',
            title: t('grid.columns.vendors'),
            width: 1,
            minWidth: 200,
            filter: 'text',
            lovData: responsibleVendorData?.namedItemsByType
                ? responsibleVendorData.namedItemsByType.map((item) => item?.name as string)
                : [],
            lovFilterContains: true,
            cells: {
                data: TooltipCell({
                    isCsv: true,
                    lovArrayField: 'vendors',
                }),
            },
        },
        {
            id: 'isCritical',
            field: 'isCritical',
            title: t('grid.columns.isCritical'),
            locked: false,
            cells: { data: YesNoCell },
            width: 1,
            minWidth: 100,
            filter: 'boolean',
        },
        {
            id: 'createdBy',
            field: 'createdBy',
            title: t('grid.columns.createdBy'),
            width: 1,
            minWidth: 150,
        },
        {
            id: 'created',
            field: 'created',
            title: t('grid.columns.created'),
            filter: 'date',
            cells: { data: DateTimeCell('DD-MMM-yyyy') },
            width: 1,
            minWidth: 130,
            cellName: 'DateCell',
        },
        {
            id: 'updatedBy',
            field: 'updatedBy',
            title: t('grid.columns.updatedBy'),
            width: 1,
            minWidth: 150,
        },
        {
            id: 'updated',
            field: 'updated',
            title: t('grid.columns.updated'),
            filter: 'date',
            cells: { data: DateTimeCell('DD-MMM-yyyy') },
            width: 1,
            minWidth: 130,
            cellName: 'DateCell',
        },
        {
            width: 1,
            minWidth: 200,
            id: 'id',
            field: 'recordId',
            title: 'ID',
            showByDefault: false,
        },
    ];

    const bulkUpdateAvailableFields = [
        { field: 'critical', type: 'boolean', required: true, textLimit: 0 },
        { field: 'active', type: 'boolean', required: true, textLimit: 0 },
        { field: 'justification', type: 'text', required: false, textLimit: 600 },
        { field: 'type', type: 'ctqTypeLov', required: false, textLimit: 600 },
        { field: 'edcEntry', type: 'edcEntryLov', required: false, textLimit: 0 },
        { field: 'responsibleVendors', type: 'responsibleVendorLov', required: false, textLimit: 0 },
        { field: 'system', type: 'text', required: false, textLimit: 70 },
        { field: 'dataSource', type: 'text', required: false, textLimit: 500 },
    ];

    if (error) return <ErrorMessage />;

    if (!data) return <Loading />;

    return (
        <>
            <Grid
                excludeHiddenRowMenuActions
                title={t('grid.title')}
                maxHeight="auto"
                testDataId="ctqf-grid"
                data={flattenedCriticalVariables}
                columns={columns}
                loading={loading}
                useLoadingOverlay
                scrollable="virtual"
                showAddIcon
                selectable={{ enabled: true, mode: 'multiple', drag: true }}
                preSelectionChange={discardCommentSideBarChanges}
                onSelectionChange={onSelectionChange}
                defaultSelectedItem={
                    scrollToSelectedCtq.current && current.selectionState.bulkUpdateRows?.length
                        ? current.selectionState.selectedItem
                        : undefined
                }
                dataItemKey="id"
                canEdit={canEdit}
                addCallback={addCallback}
                showBulkUpdateIcon
                rowMenuActions={[
                    {
                        id: 1,
                        label: t('grid.actions.edit'),
                        onClick: (dataItem: unknown) => {
                            const obs = dataItem as CriticalVariableFields;
                            createdOrUpdatedCtqId.current = null;
                            ctQfId.current = obs.id ?? null;
                            setCtqFactor(obs);
                            showEditDialog.setTrue();
                        },
                        disabled: !canEdit,
                        icon: ['far', 'pen-to-square'],
                    },
                    {
                        id: 2,
                        label: t('grid.actions.assign'),
                        onClick: (dataItem: unknown) => {
                            const cv = dataItem as CriticalVariableFields;
                            ctQfId.current = cv?.id ?? null;
                            createdOrUpdatedCtqId.current = null;
                            assignRiskClicked.current = false;
                            setCtqFactor(cv);
                            showAssignDialog.setTrue();
                            if (current.riskGridIsFiltered && cv.id === ctqFactor?.id)
                                showFilterAppliedDialog.setTrue();
                        },
                        icon: ['far', 'add'] as IconProp,
                        disabled: !canEdit,
                    },
                    {
                        id: 3,
                        label: `${t('grid.actions.assignFdr')}`,
                        onClick: (dataItem: unknown) => {
                            const cv = dataItem as CriticalVariableFields;
                            ctQfId.current = cv?.id ?? null;
                            createdOrUpdatedCtqId.current = null;
                            setCtqFactor(cv);
                            showAssignFunctionalDataReviewsDialog.setTrue();
                            if (current.functionalDataReviewsGridIsFiltered && cv.id === ctqFactor?.id)
                                showFilterAppliedDialog.setTrue();
                        },
                        icon: ['far', 'add'] as IconProp,
                        disabled: !canEdit,
                    },
                    {
                        id: 4,
                        label: t('grid.actions.delete'),
                        onClick: (dataItem: unknown) => {
                            const obs = dataItem as CriticalVariableFields;
                            createdOrUpdatedCtqId.current = null;
                            ctQfId.current = obs.id ?? null;
                            setCtqFactor(obs);
                            showDeleteDialog.setTrue();
                        },
                        icon: ['fas', 'trash-xmark'],
                        colorProp: 'Red',
                        disabled: !canEdit,
                    },
                ]}
                exportFilename={`${exportFileName}_${study?.studyNumber}_${stringDateWithoutTimeFromCurrentUtcDate(
                    'DD-MMM-YYYY',
                )}`}
                service={AuditServiceType.CORE}
                area={AuditApplicationArea.RISK_ASSESSMENT}
                dataType={AuditDataType.EXCEL_EXPORT}
                onBulkUpdateClicked={onBulkUpdateClicked}
                filterListener={(newFilterState) => {
                    if (newFilterState?.filters) {
                        setSelectionState((old) => ({
                            ...old,
                            selectedItemsCount: 0,
                            bulkUpdateRows: [],
                        }));
                    }
                }}
                filter={filterState}
                persistentStorageKey={String(PersistentStorageKeys['ctq-risk-child-grid'])}
            />
            {showFilterAppliedDialog.value && (
                <ConfirmationDialog
                    dataTestId="confirm-add-ctqf"
                    title={t('grid.filterSortWarning.title')}
                    onSubmit={() => {
                        showFilterAppliedDialog.setFalse();
                    }}
                    onClose={() => {
                        showAddDialog.setFalse();
                        showEditDialog.setFalse();
                        showDeleteDialog.setFalse();
                        showBulkUpdateDialog.setFalse();
                        showAssignFunctionalDataReviewsDialog.setFalse();
                        showFilterAppliedDialog.setFalse();
                        showAssignDialog.setFalse();
                        showAddRiskDialog.setFalse();
                    }}
                    primaryBtnLabel={t('common:continue')}
                >
                    {t('grid.filterSortWarning.body')}
                </ConfirmationDialog>
            )}
            {showBulkUpdateDialog.value && !showFilterAppliedDialog.value && (
                <BulkUpdateDialog
                    studyRef="null"
                    onClose={() => {
                        showBulkUpdateDialog.setFalse();
                    }}
                    onSubmit={onSubmitBulkUpdate}
                    dialogTitle={t('bulkUpdate.title')}
                    warningMessage={t('bulkUpdate.warningMessage', {
                        count: current.selectionState.bulkUpdateRows.length,
                    })}
                    fields={bulkUpdateAvailableFields}
                    unsavedChangesArea="general"
                />
            )}
            {showAddDialog.value && !showFilterAppliedDialog.value && (
                <AddCriticaltoQualityFactorDialog
                    onSubmit={onSubmit}
                    onClose={onAddClose}
                    onAddFunctionalReviewsSubmit={onAddFunctionalReviewsSubmit}
                />
            )}
            {showEditDialog.value && !showFilterAppliedDialog.value && ctqFactor && (
                <EditCriticaltoQualityFactorDialog
                    onSubmit={onSubmitEdit}
                    onClose={onEditClose}
                    ctqFactor={ctqFactor}
                />
            )}
            {showDeleteDialog.value && !showFilterAppliedDialog.value && ctqFactor && (
                <DeleteCriticalToQualityFactorDialog
                    onSubmit={submitDelete}
                    onClose={() => {
                        showDeleteDialog.setFalse();
                    }}
                    ctqFactor={ctqFactor}
                />
            )}
            {!showEditDialog.value &&
                showAssignDialog.value &&
                !showAddRiskDialog.value &&
                getFilteredRisks(assignableRisks).filteredRisks.length > 0 &&
                !showFilterAppliedDialog.value &&
                study &&
                ctqFactor && (
                    <AssignRisksToCtqfDialog
                        study={study}
                        onSubmit={onSubmitAssignRisks}
                        onClose={onAssignClose}
                        onAddRiskOpen={onAddRiskOpen}
                        associatedCriticalVariable={ctqFactor}
                        updatingRisks={updatingRisks}
                        assignableRisks={assignableRisks}
                    />
                )}
            {!showEditDialog.value &&
                showAssignFunctionalDataReviewsDialog.value &&
                !showFilterAppliedDialog.value &&
                study &&
                ctqFactor && (
                    <AssignFunctionalDataReviewToCtQfDialog
                        study={study}
                        onSubmit={onSubmitAssignFunctionalDataReviews}
                        onClose={onAssignFdRClose}
                        associatedCriticalVariable={ctqFactor}
                        updatingFunctionalDataReviews={updatingRisks}
                    />
                )}
            {!showEditDialog.value &&
                (showAddRiskDialog.value ||
                    (showAssignDialog.value && getFilteredRisks(assignableRisks).filteredRisks.length === 0)) &&
                !showFilterAppliedDialog.value &&
                !assignRiskClicked.current &&
                study &&
                ctqFactor && <AddRiskDialog onClose={onAssignClose} onSubmit={onSubmitAddRisk} studyRef={study?.id} />}
        </>
    );
};

export default CriticalToQualityGrid;
